'use client';

import {
  useCallback,
  useMemo,
  useState,
  type KeyboardEvent,
  type Ref,
  type TextareaHTMLAttributes,
} from 'react';

import { acceptIntentSuggestion } from '@/lib/engage/suggestions/accept-suggestion';
import { resolveGhostCompletion } from '@/lib/engage/suggestions/resolve-ghost-completion';
import type { IntentSuggestionDto } from '@/lib/engage/suggestions/schema';
import { WIDGET_TEXT_PRIMARY } from '@/lib/engage/widget-typography';

export type EngageGhostTextInputProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'onChange' | 'onKeyDown'
> & {
  value: string;
  onChange: (value: string) => void;
  suggestions: readonly IntentSuggestionDto[];
  /** When Chat Prompts is off, renders a plain textarea (no overlay). */
  enabled: boolean;
  /** Border/background shell when ghost overlay is active (textarea stays transparent). */
  shellClassName?: string;
  textareaRef?: Ref<HTMLTextAreaElement>;
  onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
};

/**
 * Cursor-style ghost completion over the chat textarea.
 * Tab accepts; Escape dismisses until the input changes; ↑/↓ cycle matches.
 */
export function EngageGhostTextInput({
  value,
  onChange,
  suggestions,
  enabled,
  shellClassName = '',
  textareaRef,
  onKeyDown,
  className = '',
  disabled,
  ...rest
}: EngageGhostTextInputProps) {
  const [cycleIndex, setCycleIndex] = useState(0);
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);
  // Render-time prop sentinel — reset cycle/dismiss when the typed value changes.
  const [valueEpoch, setValueEpoch] = useState(value);
  if (value !== valueEpoch) {
    setValueEpoch(value);
    setCycleIndex(0);
    if (dismissedFor !== null && dismissedFor !== value) {
      setDismissedFor(null);
    }
  }

  const ghost = useMemo(() => {
    if (!enabled || disabled) return null;
    if (dismissedFor === value) return null;
    return resolveGhostCompletion(value, suggestions, cycleIndex);
  }, [enabled, disabled, dismissedFor, value, suggestions, cycleIndex]);

  const acceptGhost = useCallback(() => {
    if (!ghost) return;
    const result = acceptIntentSuggestion(ghost.suggestion);
    onChange(result.text);
    setDismissedFor(null);
    setCycleIndex(0);
  }, [ghost, onChange]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (enabled && ghost) {
      if (e.key === 'Tab' && !e.shiftKey) {
        e.preventDefault();
        acceptGhost();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setDismissedFor(value);
        return;
      }
      if (e.key === 'ArrowDown' && ghost.matchCount > 1) {
        e.preventDefault();
        setCycleIndex((i) => i + 1);
        return;
      }
      if (e.key === 'ArrowUp' && ghost.matchCount > 1) {
        e.preventDefault();
        setCycleIndex((i) => i - 1);
        return;
      }
    }
    onKeyDown?.(e);
  };

  return (
    <div
      className={`relative min-h-[44px] flex-1 ${shellClassName}`}
      data-s2r-component="widget-intent-ghost-input"
    >
      {enabled ? (
        <div
          aria-hidden
          className={`pointer-events-none absolute inset-0 z-0 max-h-28 overflow-hidden whitespace-pre-wrap break-words px-3 py-2 ${WIDGET_TEXT_PRIMARY}`}
        >
          <span className="text-transparent">{value}</span>
          {ghost ? (
            <span className="text-zinc-400 dark:text-zinc-500">{ghost.ghostSuffix}</span>
          ) : null}
        </div>
      ) : null}
      <textarea
        ref={textareaRef}
        className={`relative z-10 ${className}`}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={enabled ? handleKeyDown : onKeyDown}
        {...rest}
      />
      {ghost ? (
        <p className="sr-only" aria-live="polite">
          Suggestion available. Press Tab to accept
          {ghost.matchCount > 1
            ? `, or arrow keys to cycle (${ghost.matchIndex + 1} of ${ghost.matchCount})`
            : ''}
          .
        </p>
      ) : null}
    </div>
  );
}
